<template>
  <CModal
    :show.sync="isOpen"
    size="lg"
    @close="handleClose"
    :close-on-backdrop="!submitting"
  >
    <template #header>
      <h5 class="modal-title">
        <CIcon name="cilPrint" class="mr-2"/>
        <translate translate-context="edge-prints.print-summary">Print Summary</translate>
      </h5>
    </template>

    <div class="p-3">

      <!-- Item blocks -->
      <div class="order-items-list">
        <div v-for="(item, index) in groupedItems" :key="item.orderItemId" class="order-item-block">
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <div class="item-header">
                <strong>{{ item.productName }}</strong>
              </div>

              <div class="item-format">
                <CIcon name="cilPrint" class="mr-2 text-primary"/>
                <span class="format-text">{{ item.printFormat }}</span>
                <span v-if="item.printFinish" class="ml-2">|</span>
                <span v-if="item.printFinish" class="format-text ml-2">{{ item.printFinish }}</span>
              </div>

              <div class="item-photos">
                <CIcon name="cipPicture" class="mr-1 text-muted"/>
                <span :class="{ 'text-warning font-weight-bold': item.photoCount < item.totalAvailable }">
                  <translate translate-context="edge-prints.print-summary"
                            :translate-params="{selected: item.photoCount, total: item.totalAvailable}">
                    %{selected}/%{total} photos
                  </translate>
                </span>
                <CBadge v-if="item.photoCount < item.totalAvailable" color="warning" class="ml-2">
                  <translate translate-context="edge-prints.print-summary">Partial selection</translate>
                </CBadge>
                <CBadge v-if="item.requiredCopies > 1" color="warning" class="ml-2">
                  <translate translate-context="edge-prints.print-summary"
                            :translate-params="{qty: item.requiredCopies}">
                    Qty: %{qty}
                  </translate>
                </CBadge>
              </div>
            </div>

            <!-- Copy selector for this item -->
            <div class="item-copy-selector">
              <div class="d-flex align-items-center">
                <small class="text-muted mr-2">
                  <translate translate-context="edge-prints.print-summary">Copies:</translate>
                </small>
                <CButtonGroup size="sm">
                  <CButton
                    v-if="getItemCopies(item.orderItemId) === 1"
                    color="secondary"
                    @click="removeItem(index)"
                    :disabled="submitting"
                    :title="$pgettext('edge-prints.print-summary', 'Remove from print queue')"
                  >
                    <CIcon name="cilTrash" size="sm"/>
                  </CButton>
                  <CButton
                    v-else
                    color="secondary"
                    @click="decrementItemCopies(item.orderItemId)"
                    :disabled="submitting"
                  >
                    <CIcon name="cilMinus" size="sm"/>
                  </CButton>
                  <CButton color="light" disabled class="px-3">
                    {{ getItemCopies(item.orderItemId) }}
                  </CButton>
                  <CButton
                    color="secondary"
                    @click="incrementItemCopies(item.orderItemId)"
                    :disabled="getItemCopies(item.orderItemId) >= 99 || submitting"
                  >
                    <CIcon name="cilPlus" size="sm"/>
                  </CButton>
                </CButtonGroup>
              </div>
            </div>
          </div>
        </div>

        <!-- Empty cart message -->
        <div v-if="groupedItems.length === 0" class="empty-cart text-center py-5">
          <CIcon name="cilCart" size="3xl" class="text-muted mb-3"/>
          <p class="text-muted">
            <translate translate-context="edge-prints.print-summary">No items to print</translate>
          </p>
        </div>
      </div>

    </div>

    <template #footer>
      <CButton color="secondary" @click="close" :disabled="submitting">
        <translate translate-context="edge-prints.print-summary">Cancel</translate>
      </CButton>
      <CButton
        color="primary"
        @click="confirmPrint"
        :disabled="submitting || totalPrints === 0"
        class="ml-2"
      >
        <CSpinner v-if="submitting" size="sm" class="mr-2"/>
        <CIcon v-else name="cilPrint" class="mr-2"/>
        <translate v-if="submitting" translate-context="edge-prints.print-summary">Creating print jobs...</translate>
        <translate v-else translate-context="edge-prints.print-summary">Print</translate>
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'PrintSummaryModal',
  data() {
    return {
      isOpen: false,
      groupedItems: [],
      submitting: false,
      copiesToPrint: 1,
      itemCopies: {} // Track copies per order item
    }
  },
  computed: {
    ...mapGetters('edgePrints', [
      'getMediaForOrderItem'
    ]),

    totalPhotos() {
      if (!this.groupedItems) return 0
      return this.groupedItems.reduce((sum, item) => sum + item.mediaIds.length, 0)
    },

    totalPrints() {
      if (!this.groupedItems) return 0
      return this.groupedItems.reduce((sum, item) => {
        const copies = this.getItemCopies(item.orderItemId)
        return sum + (item.photoCount * copies)
      }, 0)
    },

    hasMultipleCopies() {
      return this.groupedItems.some(item => item.orderItem.quantity > 1)
    },

    maxCopiesNeeded() {
      return Math.max(...this.groupedItems.map(item => item.orderItem.quantity || 1), 1)
    },

    hasVariedQuantities() {
      const quantities = this.groupedItems.map(item => item.orderItem.quantity || 1)
      return new Set(quantities).size > 1
    }
  },
  methods: {
    ...mapActions('edgeOrders', [
      'deselectAllMediaForOrderItem'
    ]),
    ...mapActions('edgePrints', ['createPrintJobs', 'clearAllSelections']),

    open(groupedItems) {
      if (!Array.isArray(groupedItems)) {
        console.error('PrintSummaryModal expects an array of grouped items')
        return
      }

      this.groupedItems = groupedItems.map(item => {
        // Get the order item ID from the orderItem object
        const orderItemId = item.orderItem.id

        // Get total available media for this order item
        const mediaData = this.getMediaForOrderItem(orderItemId)
        const totalAvailable = mediaData?.media?.length || item.mediaIds.length

        // Get the print config for finish
        const config = item.orderItem.product_variant?.config || item.orderItem.product?.config

        return {
          ...item,
          orderItemId,  // Add the orderItemId field
          productName: this.getProductName(item.orderItem),
          printFormat: this.getPrintFormat(item.orderItem),
          printFinish: config?.finish || '',  // Add finish (e.g., "Glossy")
          variantName: this.getVariantName(item.orderItem),
          photoCount: item.mediaIds.length,  // Selected count
          totalAvailable,  // Total available in order item
          requiredCopies: item.orderItem.quantity || 1
        }
      })

      // Initialize item copies based on required quantity
      this.itemCopies = {}
      this.groupedItems.forEach(item => {
        this.$set(this.itemCopies, item.orderItemId, item.requiredCopies)
      })

      this.isOpen = true
    },

    close() {
      if (this.submitting) {
        return // Prevent closing while submitting
      }
      this.isOpen = false
      this.groupedItems = []
      this.itemCopies = {}
      this.copiesToPrint = 1
    },

    handleClose() {
      // Called when user tries to close the modal (X button or backdrop click)
      if (this.submitting) {
        // Show a warning that print jobs are being created
        this.notifyWarning(this.$pgettext('edge-prints.print-summary', 'Please wait while print jobs are being created'))
        return
      }
      this.close()
    },

    getItemCopies(orderItemId) {
      return this.itemCopies[orderItemId] || 1
    },

    incrementItemCopies(orderItemId) {
      const current = this.itemCopies[orderItemId] || 1
      if (current < 99) {
        this.$set(this.itemCopies, orderItemId, current + 1)
      }
    },

    decrementItemCopies(orderItemId) {
      const current = this.itemCopies[orderItemId] || 1
      if (current > 1) {
        this.$set(this.itemCopies, orderItemId, current - 1)
      }
    },

    removeItem(index) {
      if (this.submitting) return

      const removedItem = this.groupedItems[index]

      // Remove from groupedItems array
      this.groupedItems.splice(index, 1)

      // Remove from itemCopies
      this.$delete(this.itemCopies, removedItem.orderItemId)

      // Deselect media for this order item
      if (removedItem.orderItem) {
        this.deselectAllMediaForOrderItem(removedItem.orderItem.id)
      }

      // If no items left, close the modal
      if (this.groupedItems.length === 0) {
        this.close()
      }
    },

    getProductName(orderItem) {
      if (!orderItem) return ''
      if (orderItem.bundle) {
        return orderItem.bundle.name
      } else if (orderItem.product) {
        return orderItem.product.name
      }
      return ''
    },

    getPrintFormat(orderItem) {
      if (!orderItem) return ''
      const config = orderItem.product_variant?.config || orderItem.product?.config
      if (config?.format_cm) {
        return config.format_cm
      }
      return '-'
    },

    getVariantName(orderItem) {
      if (!orderItem) return ''
      if (orderItem.product_variant?.name) {
        return orderItem.product_variant.name
      }
      return ''
    },

    async confirmPrint() {
      if (!this.groupedItems || this.groupedItems.length === 0) return

      this.submitting = true
      try {
        // Prepare all order items data for bulk print job creation
        const orderItemsData = this.groupedItems.map(item => {
          const data = {
            orderItem: item.orderItem,
            order: item.order,
            mediaIds: item.mediaIds,
            copies: this.getItemCopies(item.orderItemId)
          }

          // Check if this is a print job reprint
          if (item.isPrintJobReprint && item.originalPrintJob) {
            data.isPrintJobReprint = true
            data.originalPrintJob = item.originalPrintJob
          }

          return data
        })

        // Single API call for all order items
        await this.createPrintJobs(orderItemsData)

        const totalPhotos = this.totalPhotos
        this.notifySuccess(
          this.$ngettext(
            'Print job created for %{count} photo',
            'Print jobs created for %{count} photos',
            totalPhotos
          ).replace('%{count}', totalPhotos)
        )

        // Clear all selections (both order items and print jobs) after successful print
        this.clearAllSelections()

        this.$emit('print-completed')

        // Reset submitting before closing to allow the modal to close
        this.submitting = false
        this.close()
      } catch (error) {
        console.error('Failed to create print jobs:', error)
        this.notifyError(this.$pgettext('edge-prints.print-summary', 'Failed to send photos to print'))
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.order-items-list {
  max-height: 400px;
  overflow-y: auto;
}

.order-item-block {
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  background: white;
  transition: all 0.2s;
  margin-bottom: 0.75rem;
}

.order-item-block:hover {
  border-color: #007bff;
  box-shadow: 0 2px 8px rgba(0,123,255,0.1);
}

.empty-cart {
  padding: 3rem 1rem;
}

.item-header {
  font-size: 0.95rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e9ecef;
}

.item-format {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
}

.format-text {
  font-weight: 600;
  color: #007bff;
}

.variant-text {
  color: #6c757d;
  font-size: 0.9rem;
}

.item-copy-selector {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-left: 1rem;
}

.item-photos {
  font-size: 0.95rem;
  color: #495057;
  display: flex;
  align-items: center;
}

.total-summary {
  font-size: 1.1rem;
}
</style>